import React from 'react';
import { setListListener } from '../../../utils/firebase';
import AllBudgetsPageTemplate from './template';
import { Unsubscribe } from 'firebase/firestore';
import Budget from '../../../types/TeamCalculator/Budget';
import { Input } from '../../ui/Input';
import Button from '../../ui/Button';
import TeamItem from '../../../types/TeamCalculator/TeamItem';
import { Constants } from '../../ui/TeamCalculator/utils/consts';
import { LoadingIcon } from '../../ui/LoadingIcon';

type AllBudgetsPageComponentProps = {
  isLogged: boolean;
  setIsLogged: (bool: boolean) => void;
};

const AllBudgetsPageComponent: React.FC<AllBudgetsPageComponentProps> = ({
  isLogged,
  setIsLogged,
}) => {
  // State
  const [updateBudge, setUpdateBudget] = React.useState({});
  const forceUpdate = React.useCallback(() => setUpdateBudget({}), []);

  const [loadingPage, setLoadingPage] = React.useState(true);
  const [loading, setLoading] = React.useState(false);
  const [budgetsWithMembers, setBudgetsWithMembers] = React.useState<Budget[]>([]);
  const [allConstants, setAllConstants] = React.useState<Constants[] | null>(null);

  const budgets = React.useRef<Budget[]>([]);
  const budgetsListened = React.useRef<string[]>([]);
  const budgetsListener = React.useRef<null | Unsubscribe>(null);
  const unsubAllConstants = React.useRef<null | Unsubscribe>(null);

  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [error, setError] = React.useState('');

  const onSubmit = React.useCallback(() => {
    setError('');
    if (
      email === process.env.NEXT_PUBLIC_ADMIN_EMAIL &&
      password === process.env.NEXT_PUBLIC_ADMIN_PASSWORD
    ) {
      localStorage.setItem('allowed-admin', 'true');
      setIsLogged(true);
    } else {
      setError('Credenciais inválidas');
      setEmail('');
      setPassword('');
    }
  }, [email, password]);

  // Listeners
  React.useEffect(() => {
    // Check only on browser
    if (process.browser) {
      if (localStorage.getItem('allowed-admin') === 'true') {
        setIsLogged(true);
      }
    }
    setLoadingPage(false);

    return () => {
      if (unsubAllConstants.current) {
        unsubAllConstants.current();
      }
      if (budgetsListener.current) {
        budgetsListener.current();
      }
    };
  }, []);

  React.useEffect(() => {
    if (isLogged) {
      setListListener(
        ['budgets'],
        (data: Budget[]) => {
          setBudgetsWithMembers(
            data.map((updatedBudget) => ({
              ...updatedBudget,
              members: budgets.current.find((oldBudget) => updatedBudget?.id === oldBudget?.id)
                ?.members,
            })),
          );
          budgets.current = data;
          forceUpdate();
        },
        budgetsListener,
        setLoading,
      );
      setListListener(['constants'], setAllConstants, unsubAllConstants, setLoading);
    }
  }, [isLogged]);

  React.useEffect(() => {
    // Manually adding members to each budget
    if (isLogged) {
      const budgetIds = budgets.current.map((budget) => budget.id);
      // For each budget, add a listener for the members (sub-collection)
      budgetIds.forEach((id) => {
        // Just add if it not on the list already (avoid duplications)
        if (!budgetsListened.current.includes(id)) {
          // New budget member
          setListListener(['budgets', id, 'members'], (members: TeamItem[]) => {
            // Add it to the list
            budgetsListened.current = [...budgetsListened.current, id];

            // Add members to the list
            setBudgetsWithMembers(
              budgets.current.map((budget) => {
                if (budget.id === id) {
                  budget.members = members;
                  return budget;
                }
                return budget;
              }),
            );
          });
        }
      });
    }
  }, [updateBudge]);

  return (
    <>
      {!loadingPage ? (
        isLogged ? (
          <AllBudgetsPageTemplate
            budgets={budgetsWithMembers}
            allConstants={allConstants}
            loading={loading}
          />
        ) : (
          <form
            onSubmit={(e) => e.preventDefault()}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              alignItems: 'center',
              maxWidth: 500,
              margin: 'auto',
              padding: '100px 10px',
              textAlign: 'center',
            }}
          >
            Faça o login para continuar
            <Input
              placeholder="Email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <Input
              placeholder="Senha"
              value={password}
              type="password"
              onChange={(event) => setPassword(event.target.value)}
            />
            {error ? <p style={{ color: 'red' }}>{error}</p> : null}
            <Button onClick={() => onSubmit()}>Entrar</Button>
          </form>
        )
      ) : (
        <div style={{ padding: '200px' }}>
          <LoadingIcon />
        </div>
      )}
    </>
  );
};

export default AllBudgetsPageComponent;
